import { useEffect } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useApplyCoupon, useRemoveCoupon } from '@hooks/cart';
import { useCartWithOffer } from '@hooks/cart/useCartWithOffer';
import { usePhrases } from '@hooks/context/usePhrases';

interface usePromoCodeProps {
  logger: any;
  couponErrorMessages: Record<string, string>;
  isFinanceContractType: boolean;
}

export const usePromoCode = ({ logger, couponErrorMessages, isFinanceContractType }: usePromoCodeProps) => {
  const errorPhrases = usePhrases({ name: 'errors', rawObject: true });
  const { data: cartDataWithOffer } = useCartWithOffer();
  const cart = cartDataWithOffer;
  const coupon = cart?.coupon;

  const removeCouponMutation = useRemoveCoupon();
  const applyCouponMutation = useApplyCoupon();

  const formOptions = {
    defaultValues: {
      isPromoCode: !!coupon?.code,
      promoCode: { code: cart?.couponCode?.code || coupon?.code || '' },
    },
    resolver: yupResolver(
      Yup.object().shape({
        isPromoCode: Yup.boolean(),
        promoCode: Yup.object().when('isPromoCode', {
          is: true,
          then: Yup.object().shape({
            code: Yup.string(),
          }),
        }),
      })
    ),
  };

  const methods = useForm(formOptions);

  const addPromoCode = async couponCode => {
    try {
      await applyCouponMutation.mutateAsync({ couponCode });
      methods.clearErrors('promoCode.code');
    } catch (error) {
      const errorMessage = couponErrorMessages[error.data?.errors[0]?.code];
      const genericErrorMessage = errorPhrases?.['generic-error']?.value;

      if (errorMessage || genericErrorMessage) {
        methods.setError('promoCode.code', { message: errorMessage || genericErrorMessage });
      }
    }
  };

  const checkPromoCode = () => {
    const promoCode = methods.getValues('promoCode.code');

    addPromoCode(promoCode);
  };

  const removePromoCode = async () => {
    try {
      await removeCouponMutation.mutateAsync();
    } catch (error) {
      const errorMessage = couponErrorMessages[error.data?.errors[0]?.code];
      const genericErrorMessage = errorPhrases?.['generic-error']?.value;

      if (errorMessage || genericErrorMessage) {
        methods.setError('promoCode.code', {
          message: errorMessage || genericErrorMessage,
        });
      }
    }
  };

  const deleteCoupon = () => {
    removePromoCode();
    methods.setValue('promoCode.code', '');
  };

  const handlePromoCodeToggle = () => {
    isChecked => !isChecked && removePromoCode();
  };

  const handleSubmit = () => {
    return methods.handleSubmit(
      async data => !isFinanceContractType && addPromoCode(data?.promoCode?.code),
      errors => logger.error({ message: 'Error in onSubmit in PromoCode', params: errors })
    )();
  };

  useEffect(() => {
    const isPromoCode = methods.getValues('isPromoCode');

    const checkTheCoupon = async () => {
      if (isPromoCode) {
        await removePromoCode();
        checkPromoCode();
      }
    };

    checkTheCoupon();
  }, []);

  useEffect(() => {
    methods.clearErrors('promoCode.code');
    if (!isFinanceContractType && coupon?.code) {
      methods.setValue('isPromoCode', !!coupon?.code);
      methods.setValue('promoCode.code', coupon?.code);
    }
  }, [coupon, isFinanceContractType]);

  if (isFinanceContractType) {
    return {
      coupon: undefined,
      methods,
      checkPromoCode: () => {},
      deleteCoupon: () => {},
      onPromoCodeToggle: () => {},
      onSubmit: () => {},
    };
  }

  return {
    coupon,
    methods,
    checkPromoCode: checkPromoCode,
    deleteCoupon,
    onPromoCodeToggle: handlePromoCodeToggle,
    onSubmit: handleSubmit,
  };
};
